import { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut as firebaseSignOut, getRedirectResult, getAdditionalUserInfo } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { trackEvent } from './analytics';

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  emailVerified: false,
  signOut: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);

  // Settle any pending redirect-flow sign-in (mobile Safari / WKWebView
  // path — SignInModal calls signInWithRedirect which navigates the whole
  // tab away, then the IdP returns us here. getRedirectResult must be
  // called once on first mount; firebase-auth then fires onAuthStateChanged
  // with the new user. Safe no-op when no redirect is pending.
  useEffect(() => {
    getRedirectResult(auth).then((result) => {
      // Redirect (mobile/PWA) sign-in completes here — fire login once so we
      // don't miss the funnel step for the redirect flow. Silent session
      // restore returns null and is (correctly) not counted as a login.
      if (result && result.user) {
        try {
          const info = getAdditionalUserInfo(result);
          const method = result.providerId ? result.providerId.replace('.com', '') : 'redirect';
          trackEvent('login', { method, is_new_user: !!(info && info.isNewUser) });
        } catch (_) { /* analytics best-effort */ }
      }
    }).catch((err) => {
      if (err && err.code && err.code !== 'auth/no-auth-event') {
        console.warn('[Auth] getRedirectResult error:', err.code, err.message);
      }
    });
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser && !firebaseUser.isAnonymous) {
        setUser(firebaseUser);
        // OAuth providers auto-verify email; only email/password users need explicit check
        const verified = firebaseUser.emailVerified ||
          firebaseUser.providerData.some(p => p.providerId !== 'password');
        setEmailVerified(verified);
        // Fetch or create Firestore profile
        try {
          const profileRef = doc(db, 'users', firebaseUser.uid);
          const snap = await getDoc(profileRef);
          if (snap.exists()) {
            setProfile(snap.data());
          } else {
            // Auto-create profile on first sign-in
            const newProfile = {
              handle: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'user',
              displayName: firebaseUser.displayName || '',
              photoUrl: firebaseUser.photoURL || '',
              email: firebaseUser.email || '',
              emailVerified: verified,
              publisherTier: 'community',
              role: 'user',
              status: 'active',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            };
            await setDoc(profileRef, newProfile);
            setProfile(newProfile);
          }
        } catch (err) {
          console.warn('[Auth] Failed to load/create profile:', err.message);
          setProfile(null);
        }
      } else {
        setUser(null);
        setProfile(null);
        setEmailVerified(false);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleSignOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setProfile(null);
    } catch (err) {
      console.error('[Auth] Sign out error:', err);
    }
  };

  const refreshProfile = async () => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) return;
    try {
      const snap = await getDoc(doc(db, 'users', u.uid));
      if (snap.exists()) setProfile(snap.data());
    } catch (err) {
      console.warn('[Auth] Failed to refresh profile:', err.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, emailVerified, signOut: handleSignOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  return useContext(AuthContext);
}

export default AuthContext;
